import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import {
  BaseTextField,
  Button,
  FormControl,
  Modal,
  ModalActions,
  ModalContent,
  ModalTitle,
  Select,
  Spinner,
} from '@ifs-gcp/components';

import styles from './modals.module.scss';

export interface DocumentUploadModalResult {
  displayName?: string;
  language?: string;
}

export interface LanguageOption {
  value: string;
  label: string;
}

interface DocumentUploadModalProps {
  typeName: string;
  isNameRequired: boolean;
  isLanguageSensitive: boolean;
  languageOptions: LanguageOption[];
  isUploading: boolean;
  translations: Record<string, string>;
  onContinue: (result: DocumentUploadModalResult) => void;
  onClose: () => void;
}

interface FormValues {
  displayName: string;
  language: string;
}

export const DocumentUploadModal = ({
  typeName,
  isNameRequired,
  isLanguageSensitive,
  languageOptions,
  isUploading,
  translations,
  onContinue,
  onClose,
}: DocumentUploadModalProps) => {
  const {
    i18n: { language },
  } = useTranslation();
  const defaultLanguage = languageOptions.find((option) => option.value === language);
  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm<FormValues>({
    mode: 'onChange',
    defaultValues: { displayName: '', language: defaultLanguage?.value },
  });

  const onSubmit = (values: FormValues) => {
    onContinue({
      displayName: isNameRequired ? values.displayName.trim() : undefined,
      language: isLanguageSensitive ? values.language : undefined,
    });
  };

  return (
    <Modal className={styles.modal} open>
      <ModalTitle className={styles.modalTitle} onClose={onClose}>
        {translations.upload}: {typeName}
      </ModalTitle>

      <ModalContent className={styles.content}>
        {isNameRequired && (
          <Controller
            name="displayName"
            control={control}
            rules={{
              required: true,
              minLength: { value: 5, message: translations.fileNameHint },
              maxLength: { value: 50, message: translations.fileNameHint },
            }}
            render={({ field }) => (
              <FormControl
                className={styles.fileNameInput}
                error={Boolean(errors.displayName)}
                label={translations.fileNameLabel}
                helperText={errors.displayName?.message ?? translations.fileNameHint}
              >
                <BaseTextField
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  maxLength={50}
                  fullWidth
                />
              </FormControl>
            )}
          />
        )}

        {isLanguageSensitive && (
          <Controller
            name="language"
            control={control}
            rules={{ required: translations.languagePlaceholder }}
            render={({ field, fieldState: { error } }) => (
              <FormControl
                className={styles.fileNameInput}
                label={translations.languageLabel}
                error={Boolean(error)}
                helperText={error?.message}
              >
                <Select
                  variant="outlined"
                  MenuProps={{
                    slotProps: {
                      paper: {
                        className: styles.paper,
                        sx: { maxHeight: 350 },
                      },
                    },
                  }}
                  className={styles.selectWrapper}
                  classes={{
                    icon: styles.selectIcon,
                  }}
                  value={field.value}
                  displayEmpty
                  placeholder={translations.languagePlaceholder}
                  options={languageOptions}
                  renderValue={(value) =>
                    value ? (languageOptions.find((o) => o.value === value)?.label ?? value) : ''
                  }
                  handleMenuItemClick={field.onChange}
                />
              </FormControl>
            )}
          />
        )}
      </ModalContent>

      <ModalActions>
        <Button
          variant="contained"
          color="secondary"
          disabled={!isValid || isUploading}
          onClick={handleSubmit(onSubmit)}
          startIcon={isUploading ? <Spinner size={16} /> : null}
        >
          {translations.continue}
        </Button>
      </ModalActions>
    </Modal>
  );
};
